import type { Activity, ActivityType, EmbeddedCompany } from 'types';
import type { Category } from 'modules/supplier/category/types';
import type { ChainNode, ChainPath } from 'modules/supplier/supplier/components/ProductChainPicker';
import type { MediaProductNode, MediaTargetPath } from 'modules/agreement/discountTree';
import type { DiscountTreeNode } from 'modules/agreement/discountTree';
import type { ClientAgreementVendorTerms } from '../vendorTerms/types';
import type { ClientDiscountOwner } from './owner';
import { discountContexts, type DiscountContext } from 'modules/agreement/discountTree/form';
import type { FactorApplicationPolicy } from 'modules/agreement/factorPolicy/form';

type ServiceCategoryNode = Extract<ChainNode, { kind: 'service-category' }>;
type ProductNode = Extract<ChainNode, { kind: 'product' }>;
export type ServiceProductNode = Omit<ProductNode, 'productKind'> & { productKind: 'SERVICE' };

/** Complete catalogue paths for either client discount hierarchy. */
export type ClientTargetPath =
  | MediaTargetPath
  | readonly [ServiceCategoryNode]
  | readonly [ServiceCategoryNode, ServiceProductNode];
export type { MediaProductNode };

export type ClientAgreementDiscountType = { id: ActivityType; label: string };

export const contexts = discountContexts;
export type ClientDiscountContext = DiscountContext;

export type ClientDiscountPair = {
  client: string | null;
  provider: string | null;
};

export type ClientAgreementDiscountRuleInput = {
  /** Full current chain, ending at the saved endpoint. */
  target_path: ClientTargetPath;
  target_activity: Activity | null;
  target_category: Category | null;
  standard_client_discount: string | null;
  standard_provider_discount: string | null;
  non_standard_client_discount: string | null;
  non_standard_provider_discount: string | null;
  package_client_discount: string | null;
  package_provider_discount: string | null;
  client_factor_policy: FactorApplicationPolicy | null;
  provider_factor_policy: FactorApplicationPolicy | null;
  terms: string;
};

export type ClientAgreementDiscountRule = ClientAgreementDiscountRuleInput & {
  id: number;
  order: number;
};

export type ClientDiscountTarget = { kind: ChainNode['kind']; id: number };
export type ClientDiscountRuleWrite = Omit<ClientAgreementDiscountRuleInput, 'target_path'> & {
  target: ClientDiscountTarget;
  provider?: EmbeddedCompany | null;
  root_id?: number;
};
export type ClientDiscountSaveInput = ClientAgreementDiscountRuleInput & { provider?: EmbeddedCompany | null };
export type ClientDiscountTreeNode = DiscountTreeNode<ClientAgreementDiscountRule>;
export type ClientDiscountTreeContext = {
  root: ClientAgreementVendorTerms;
  owner: ClientDiscountOwner;
  isRoot: boolean;
};
export type ClientDiscountRootNode = DiscountTreeNode<ClientAgreementDiscountRule, ClientDiscountTreeContext>;

export type ClientRuleFormValues = Omit<ClientAgreementDiscountRuleInput, 'target_path'> & {
  target_path: ClientTargetPath | null;
  provider?: EmbeddedCompany | null;
};

/** Keeps the transport path compatible with picker utilities. */
export type ClientChainPath = ChainPath;
